import { useState, useEffect, useMemo } from 'react'
import MaterialTable from '@material-table/core'
import { makeLeadsGetRequest, makeGetRequest } from 'utils/api'
import { timezoneDate } from 'utils/helpers'
import { NumericFormat } from 'react-number-format'
import { getCurrency } from 'utils/authService'
import { useNavigate } from 'react-router'
import { useFirstRender } from 'hooks'
import { FaArchive } from 'react-icons/fa'
import { MdUnarchive } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toUsers } from '../LeadForm/serializers'

const List = ({
  tableRef,
  startDateEnabled,
  startDate,
  endDate,
  startFollowupDateEnabled,
  startFollowupDate,
  endFollowupDate,
  startLastTouchedDateEnabled,
  startLastTouchedDate,
  endLastTouchedDate,
  isArchived,
  toggleArchiveLead,
  deleteLead,
  setSelectedLeads,
  toggleEmailLead,
  filteredByUser
}) => {
  const navigate = useNavigate()
  const firstRender = useFirstRender()
  const storedUsers = useSelector(state => state.users.users)
  const [users, setUsers] = useState(toUsers(storedUsers) || [])

  const columns = useMemo(
    () => [
      { title: 'Status', field: 'status', render: row => row.status?.name || '' },
      {
        title: 'Lead Owner',
        field: 'createdBy',
        render: row => users.find(u => u.value === row.assigneeId)?.label || ''
      },
      {
        title: 'Name',
        field: 'name',
        cellStyle: { minWidth: 250, width: 300, maxWidth: 400 },
        render: row => <Link to={`/leads/${row.id}`}>{row.name}</Link>
      },
      {
        title: 'Date Entered',
        field: 'createdOn',
        defaultSort: 'desc',
        render: row => timezoneDate(row.createdOn)
      },
      {
        title: 'Follow-up Date',
        field: 'followUpDate',
        render: row => (row.followUpDate ? timezoneDate(row.followUpDate) : '')
      },
      {
        title: 'Last Touched On',
        field: 'lastTouchedOn',
        render: row => (row.lastTouchedOn ? timezoneDate(row.lastTouchedOn) : '')
      },
      { title: 'Source', field: 'source', render: row => row.source?.name || '' },
      { title: 'First Name', field: 'contactFirstName' },
      { title: 'Last Name', field: 'contactLastName' },
      { title: 'Company', field: 'company' },
      { title: 'Email', field: 'email' },
      { title: 'Phone', field: 'phone' },
      { title: 'Mobile Phone', field: 'mobilePhone' },
      { title: 'Office Phone', field: 'officePhone' },
      {
        title: 'Website',
        field: 'website',
        cellStyle: { minWidth: 250, width: 300, maxWidth: 400 }
      },
      {
        title: 'Tags',
        field: 'tags',
        render: row => row.tags?.map(tag => tag.name).join(', ')
      },
      { title: 'Temp', field: 'temperature', render: row => row.state?.name || '' },
      {
        title: 'Spend',
        field: 'spend',
        render: row => (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.spend}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      { title: 'City', field: 'city' },
      { title: 'State / Region', field: 'region' }
    ],
    [users]
  )

  useEffect(() => {
    ;(async () => {
      if (users.length) return
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getData = async query => {
    if (firstRender) return

    const { page, pageSize, orderBy, orderDirection, search } = query

    const response = await makeLeadsGetRequest(
      `/v1/leads/list?page=${page ?? 0}&size=${pageSize}&sortByColumn=${
        orderBy?.field ?? 'createdOn'
      }&sortByValue=${orderDirection || 'desc'}&filterBy=${search}${
        startDateEnabled ? '&createdStartDate=' + startDate + '&createdEndDate=' + endDate : ''
      }${
        startFollowupDateEnabled
          ? '&followupStartDate=' + startFollowupDate + '&followupEndDate=' + endFollowupDate
          : ''
      }${
        startLastTouchedDateEnabled
          ? '&lastTouchedStartDate=' +
            startLastTouchedDate +
            '&lastTouchedEndDate=' +
            endLastTouchedDate
          : ''
      }${isArchived ? '&isArchived=true' : ''}&creators=${filteredByUser?.value ?? ''}`
    )

    return {
      data: response.content,
      page: response.page?.number ?? 0,
      totalCount: response.page?.totalElements ?? 0
    }
  }

  return (
    <div>
      <MaterialTable
        title=""
        tableRef={tableRef}
        columns={columns}
        data={query => getData(query)}
        options={{
          sorting: true,
          debounceInterval: 500,
          headerStyle: {
            backgroundColor: '#eee',
            color: '#000',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '14px'
          },
          maxBodyHeight: `${window.innerHeight - 400}px`,
          pageSize: 50,
          padding: 'dense',
          selection: true,
          showTextRowsSelected: false,
          pageSizeOptions: [5, 10, 20, 50, 100]
        }}
        style={{
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '14px'
        }}
        actions={[
          {
            icon: 'edit',
            tooltip: 'Edit Lead',
            onClick: (event, rowData) => navigate(`/leads/${rowData.id}`),
            position: 'row'
          },
          {
            action: rowData => {
              return {
                icon: 'email',
                tooltip: 'Email Lead',
                onClick: (e, rowData) => toggleEmailLead(rowData),
                disabled: !rowData.email
              }
            },
            position: 'row'
          },
          {
            action: rowData => {
              return {
                icon: () => (rowData.isArchived ? <MdUnarchive /> : <FaArchive />),
                tooltip: rowData.isArchived ? 'Unarchive Lead' : 'Archive Lead',
                onClick: () => toggleArchiveLead(rowData.id, rowData.isArchived)
              }
            },
            position: 'row'
          },
          {
            icon: 'delete',
            tooltip: 'Delete Lead',
            onClick: (event, rowData) => deleteLead(rowData.id),
            position: 'row'
          }
        ]}
        onSelectionChange={setSelectedLeads}
      />
    </div>
  )
}

export default List
